import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api.js'
import { useAuth } from '../../hooks/useAuth.js'
import { formatDate } from '../../utils/helpers'
import { useTranslation } from 'react-i18next'

const STATUS_STYLES = {
  Open: 'bg-red-100 text-red-700',
  'Under Review': 'bg-amber-100 text-amber-700',
  Resolved: 'bg-green-100 text-green-700',
  Closed: 'bg-gray-100 text-gray-500',
}

export default function DisputesList() {
  const { t } = useTranslation('ops')
  const navigate = useNavigate()
  const { user } = useAuth()
  const [disputes, setDisputes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showRaiseForm, setShowRaiseForm] = useState(false)
  const [form, setForm] = useState({ contract_id: '', subject: '', description: '' })
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState(null)
  const [contracts, setContracts] = useState([])

  const loadDisputes = () => {
    api.get('/disputes/')
      .then(r => setDisputes(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Failed to load disputes'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadDisputes()
    // Contracts for the dropdown (so admin picks by number, not raw ID)
    api.get('/contracts/').then(r => setContracts(r.data || [])).catch(() => {})
  }, [])

  const submitDispute = async e => {
    e.preventDefault()
    if (!form.contract_id || !form.subject.trim() || !form.description.trim()) {
      setFormError('All fields are required')
      return
    }
    setSubmitting(true)
    setFormError(null)
    try {
      await api.post('/disputes/', { ...form, contract_id: parseInt(form.contract_id) })
      setShowRaiseForm(false)
      setForm({ contract_id: '', subject: '', description: '' })
      loadDisputes()
    } catch (e) {
      setFormError(e.response?.data?.detail || 'Failed to raise dispute')
    } finally {
      setSubmitting(false)
    }
  }

  const resolveDispute = async (id, resolution) => {
    try {
      await api.post(`/disputes/${id}/resolve`, { resolution })
      loadDisputes()
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to resolve dispute')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  // Admin-only workflow per business decision — discreet, not visible to other roles.
  if (user?.role !== 'Admin') {
    return (
      <div className="p-6">
        <div className="max-w-md mx-auto mt-12 bg-white border border-gray-200 rounded-xl p-8 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-3">
            <span className="text-2xl">🔒</span>
          </div>
          <h2 className="text-lg font-semibold text-gray-800">{t('restricted')}</h2>
          <p className="text-sm text-gray-500 mt-2">
            Disputes are managed by Admin only. If you have a concern about your contract or deliverables, please contact your administrator.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{t('disputes')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{disputes.length} dispute{disputes.length !== 1 ? 's' : ''}</p>
        </div>
        {user?.role === 'Admin' && (
          <button
            onClick={() => setShowRaiseForm(s => !s)}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
          >
            {showRaiseForm ? 'Cancel' : '+ Log Dispute'}
          </button>
        )}
      </div>

      {/* Raise dispute form */}
      {showRaiseForm && (
        <form onSubmit={submitDispute} className="bg-white rounded-xl border border-gray-200 p-5 mb-6 space-y-4">
          <h3 className="font-semibold text-gray-700">{t('raise_new_dispute')}</h3>
          {formError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">{formError}</div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('contract_req')}</label>
            <select
              value={form.contract_id}
              onChange={e => setForm(f => ({ ...f, contract_id: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">{t('select_contract')}</option>
              {contracts.map(c => (
                <option key={c.id} value={c.id}>
                  {c.contract_number} — {c.status}{c.total_fee ? ` — $${Number(c.total_fee).toLocaleString()}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('subject_req')}</label>
            <input
              type="text"
              value={form.subject}
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              placeholder={t('ph_dispute_summary')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('description_req')}</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={4}
              placeholder={t('ph_dispute_detail')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={() => setShowRaiseForm(false)}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 text-sm bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Dispute'}
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-4">{error}</div>
      )}

      {disputes.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-4">⚖️</div>
          <p className="text-sm">{t('no_disputes')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {disputes.map(d => (
            <div key={d.id} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-800 truncate">{d.subject}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${STATUS_STYLES[d.status] || 'bg-gray-100 text-gray-600'}`}>
                      {d.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 line-clamp-2">{d.description}</p>
                  <p className="text-xs text-gray-400 mt-1.5">
                    Contract #{d.contract_id} · {d.raised_at ? formatDate(d.raised_at) : ''}
                  </p>
                  {d.resolution && (
                    <div className="mt-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                      <strong>{t('resolution_colon')}</strong> {d.resolution}
                    </div>
                  )}
                </div>
                {user?.role === 'Admin' && d.status === 'Open' && (
                  <div className="shrink-0">
                    <ResolveButton disputeId={d.id} onResolved={loadDisputes} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ResolveButton({ disputeId, onResolved }) {
  const { t } = useTranslation('ops')
  const [open, setOpen] = useState(false)
  const [resolution, setResolution] = useState('')
  const [loading, setLoading] = useState(false)

  const submit = async () => {
    if (!resolution.trim()) return
    setLoading(true)
    try {
      await api.post(`/disputes/${disputeId}/resolve`, { resolution })
      onResolved()
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to resolve')
    } finally {
      setLoading(false)
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
      >
        Resolve
      </button>
    )
  }

  return (
    <div className="flex flex-col gap-2 w-48">
      <textarea
        value={resolution}
        onChange={e => setResolution(e.target.value)}
        rows={3}
        placeholder={t('ph_resolution')}
        className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs w-full"
        autoFocus
      />
      <div className="flex gap-2">
        <button
          onClick={submit}
          disabled={loading || !resolution.trim()}
          className="flex-1 px-2 py-1 text-xs bg-green-600 text-white rounded-lg disabled:opacity-50"
        >
          {loading ? '...' : 'Confirm'}
        </button>
        <button
          onClick={() => setOpen(false)}
          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-lg text-gray-600"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
